// editorialFilter.js — Filtro central de conteúdo editorial.
//
// Cada tipo de conteúdo tem seu validador específico. A função central
// `isRenderable(item, type)` delega para o validador correto e retorna
// boolean. Também exporta `filterList(list, type)` para casos onde o
// consumidor recebe um array (planos, livros, etc.).
//
// Regra editorial: quando um item não passa no filtro, ele NÃO deve
// aparecer na UX pública — nem placeholder, nem card vazio, nem toast.
// Silêncio elegante em todos os casos.
//
// P0.3 (iter66) — versões por tipo:
//   plan                 — precisa de conteúdo real, pelo menos 1 dia com estudo
//   book                 — precisa de capa + título + descrição
//   sermon               — precisa de título + base_text
//   youtubeVideo         — precisa de video_id + title
//   palavraMinuto        — precisa de storage_path + title + scheduled_date
//   themeWeek            — precisa de available + days[≥1]
//   biblicalConnection   — precisa de ref parseável (via bibleRef.js)

import { parseBibleRef } from "./bibleRef";

function nonEmpty(s) {
  return typeof s === "string" && s.trim().length > 0;
}

// --- PLAN ---
export function isRenderablePlan(p) {
  if (!p || typeof p !== "object") return false;
  if (!p.slug && !p.id) return false;
  if (!nonEmpty(p.title)) return false;
  // Aceita `description` OU `summary` OU `subtitle`.
  const hasDescription = nonEmpty(p.description) || nonEmpty(p.summary) || nonEmpty(p.subtitle);
  if (!hasDescription) return false;
  // Status é opcional (schema legacy pode não ter). Rejeita explicitamente draft/hidden.
  if (p.status && !["published", "active"].includes(String(p.status).toLowerCase())) return false;
  // Precisa de pelo menos 1 dia publicado. `days` é a lista quando vem completa;
  // `duration_days` é o total agendado; `days_published_count` é o publicado.
  const days = Array.isArray(p.days) ? p.days : null;
  if (days) {
    // Ao menos 1 dia com conteúdo relevante (title OU reflection OU study).
    const hasContent = days.some(
      (d) =>
        d && (nonEmpty(d.title) || nonEmpty(d.reflection) || nonEmpty(d.study) || nonEmpty(d.verse_text))
    );
    if (!hasContent) return false;
  } else {
    // Sem array `days` — usar contadores/duração como proxy.
    const total = Number(p.duration_days ?? p.days_count ?? 0);
    if (!Number.isFinite(total) || total <= 0) return false;
  }
  return true;
}

// --- BOOK ---
export function isRenderableBook(b) {
  if (!b || typeof b !== "object") return false;
  if (!nonEmpty(b.title)) return false;
  if (!nonEmpty(b.cover_url)) return false;
  // Descrição OU subtitle é suficiente.
  if (!nonEmpty(b.description) && !nonEmpty(b.subtitle)) return false;
  return true;
}

// --- SERMON ---
export function isRenderableSermon(s) {
  if (!s || typeof s !== "object") return false;
  return nonEmpty(s.title) && nonEmpty(s.base_text);
}

// --- YOUTUBE VIDEO (Vídeo em Destaque + genéricos) ---
export function isRenderableYoutubeVideo(v) {
  if (!v || typeof v !== "object") return false;
  const vid = v.video_id || v.videoId || v.id;
  if (!vid || typeof vid !== "string" || !/^[A-Za-z0-9_-]{11}$/.test(vid)) return false;
  if (!nonEmpty(v.title)) return false;
  return true;
}

// --- PALAVRA EM 1 MINUTO ---
export function isRenderablePalavraMinuto(p) {
  if (!p || typeof p !== "object") return false;
  if (!nonEmpty(p.title)) return false;
  if (!nonEmpty(p.video_url)) return false;
  if (!nonEmpty(p.scheduled_date)) return false;
  return true;
}

// --- SEMANA TEMÁTICA ---
export function isRenderableThemeWeek(w) {
  if (!w || typeof w !== "object") return false;
  if (!w.available) return false;
  const days = Array.isArray(w.days) ? w.days : null;
  if (!days || days.length === 0) return false;
  return true;
}

// --- CONEXÃO BÍBLICA ---
export function isRenderableBiblicalConnection(c) {
  if (!c || typeof c !== "object") return false;
  const ref = c.ref || c.reference;
  if (!nonEmpty(ref)) return false;
  // Deve ser parseável para gerar rota válida.
  return parseBibleRef(ref) !== null;
}

const REGISTRY = {
  plan: isRenderablePlan,
  book: isRenderableBook,
  sermon: isRenderableSermon,
  youtubeVideo: isRenderableYoutubeVideo,
  palavraMinuto: isRenderablePalavraMinuto,
  themeWeek: isRenderableThemeWeek,
  biblicalConnection: isRenderableBiblicalConnection,
};

/**
 * Filtro central. Retorna true se o item pode ser renderizado.
 * Uso: `isRenderable(plan, "plan")`, `isRenderable(book, "book")` etc.
 */
export function isRenderable(item, type) {
  const validator = REGISTRY[type];
  if (!validator) return true; // tipo desconhecido — assume válido (não bloqueia)
  try {
    return validator(item);
  } catch {
    return false;
  }
}

/** Aplica o filtro a uma lista. */
export function filterList(list, type) {
  if (!Array.isArray(list)) return [];
  return list.filter((x) => isRenderable(x, type));
}
